import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Heart, MessageCircle, PawPrint, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { STATUS, aBtn, aChip, photo } from "@/lib/adopt";
import { PetCard } from "@/pages/adopcion/AdoptList";

const BADGE = { pendiente: "bg-[#F2CC8F]/50", publicado: "bg-[#81B29A]/20 text-[#2F6B52]", pausado: "bg-[#E8DCCB]", adoptado: "bg-[#81B29A] text-white", rechazado: "bg-[#FFF1EC] text-[#C9654B]" };

const Mine = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/adopciones/mine").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows && !rows.length) return <div className="text-center py-12 text-[#6B6F80]" data-testid="panel-mine-empty"><p>Aún no has publicado ningún anuncio.</p><Link to="/adopcion/publicar" className={`${aBtn} mt-4`}><Plus size={17} /> Dar en adopción</Link></div>;
  return (
    <div className="space-y-3" data-testid="panel-mine">
      {(rows || []).map((x) => (
        <Link key={x.id} to={`/adopcion/${x.id}`} className="flex items-center gap-4 bg-white rounded-2xl p-3 hover:shadow-md transition-shadow" data-testid={`panel-mine-${x.id}`}>
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#F4EBDD] shrink-0">{x.photo_count > 0 ? <img src={photo(x.id)} alt="" className="w-full h-full object-cover" /> : <PawPrint className="m-6 text-[#D9C7AE]" />}</div>
          <div className="min-w-0 flex-1">
            <p className="font-adopt text-xl text-[#3D405B] truncate">{x.name || x.title}</p>
            <p className="text-sm text-[#6B6F80] truncate">{x.title}</p>
            <span className={`inline-block mt-1 text-xs font-bold px-2.5 py-1 rounded-full ${BADGE[x.status]}`} data-testid={`panel-status-${x.id}`}>{STATUS[x.status]}</span>
          </div>
        </Link>
      ))}
    </div>
  );
};

const Favs = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/adopciones/favoritos").then((r) => setRows(r.data)); }, []);
  if (rows && !rows.length) return <p className="text-center py-12 text-[#6B6F80]" data-testid="panel-favs-empty">Pulsa el corazón en un anuncio para guardarlo aquí.</p>;
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="panel-favs">{(rows || []).map((x) => <PetCard key={x.id} x={x} />)}</div>;
};

export const Threads = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/adopcion-mensajes").then((r) => setRows(r.data)); }, []);
  if (rows && !rows.length) return <p className="text-center py-12 text-[#6B6F80]" data-testid="panel-threads-empty">Aún no tienes conversaciones.</p>;
  return (
    <div className="space-y-3" data-testid="panel-threads">
      {(rows || []).map((t) => (
        <Link key={t.id} to={`/adopcion/mensajes/${t.id}`} className="flex items-center gap-4 bg-white rounded-2xl p-4 hover:shadow-md transition-shadow" data-testid={`thread-row-${t.id}`}>
          <div className="w-12 h-12 rounded-full bg-[#F2CC8F]/50 flex items-center justify-center text-[#3D405B] font-bold shrink-0">{(t.other_name || "?")[0].toUpperCase()}</div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-[#3D405B] truncate">{t.title} <span className="font-normal text-[#6B6F80]">· {t.role === "anunciante" ? `solicitud de ${t.other_name || "un adoptante"}` : `con ${t.other_name || "el anunciante"}`}</span></p>
            <p className="text-sm text-[#6B6F80] truncate">{t.messages[0]?.text}</p>
          </div>
          {t.unread > 0 && <span className="bg-[#E07A5F] text-white text-xs font-bold rounded-full min-w-6 h-6 px-2 flex items-center justify-center" data-testid={`thread-unread-${t.id}`}>{t.unread}</span>}
        </Link>
      ))}
    </div>
  );
};

const TABS = [["anuncios", "Mis anuncios", PawPrint], ["favoritos", "Favoritos", Heart], ["mensajes", "Mensajes", MessageCircle]];

export default function AdoptPanel() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "anuncios";
  if (user === false) return <Navigate to="/login?next=%2Fadopcion%2Fpanel" replace />;
  return (
    <div className="bg-[#FFF8F0] min-h-screen" data-testid="adopt-panel">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-adopt text-4xl text-[#3D405B]">Mi espacio de adopción</h1>
          <Link to="/adopcion/publicar" className={aBtn} data-testid="panel-new"><Plus size={17} /> Nuevo anuncio</Link>
        </div>
        <div className="flex gap-2 mt-6 overflow-x-auto" data-testid="panel-tabs">
          {TABS.map(([k, l, Icon]) => <button key={k} onClick={() => setParams({ tab: k })} className={`${aChip(tab === k)} inline-flex items-center gap-2`} data-testid={`panel-tab-${k}`}><Icon size={15} /> {l}</button>)}
        </div>
        <div className="mt-6">{tab === "anuncios" ? <Mine /> : tab === "favoritos" ? <Favs /> : <Threads />}</div>
      </div>
    </div>
  );
}
